export const getTypographyVariables = (prefix = "default", typography) => {
  return {
    [`--${prefix}-font-size`]: typography.font_size + "px",
    [`--${prefix}-bold`]: typography.bold ? "bold" : "",
    [`--${prefix}-font-styles`]: typography.italic ? "italic" : "normal",
    [`--${prefix}-text-transform`]: typography.text_transform,
    [`--${prefix}-font-weight`]: typography.font_weight + "",
  };
};

export const getShadowValue = (shadow) => {
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`;
};

export const getPaddingValue = (padding) => {
  return `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`;
};

/**
 * Expand WordPress' preset shorthand into a usable CSS value.
 *
 * Controls store palette and spacing picks as `var:preset|<type>|<slug>`, which
 * is not valid CSS — assigned raw, the browser discards the declaration and the
 * stylesheet default shows instead. Mirrors Helpers::expand_preset_ref() in PHP.
 *
 * @param {string} value Attribute value.
 * @return {string} CSS value, or the original value when it is not a reference.
 */
// Matches the fallback in Slider/render.php and the `gap` attribute default.
export const SLIDER_GAP_FALLBACK = "16px";

export const resolvePresetValue = (value) => {
  if (typeof value !== "string" || !value.startsWith("var:")) {
    return value;
  }
  return `var(--wp--${value.slice(4).replace(/\|/g, "--")})`;
};

/**
 * A slider's gap attribute, resolved to a length the width formula can divide by.
 *
 * Mirrors the validation in Slider/render.php, and for the same reason
 * getBlockGapValue does: an editor that reads the attribute differently from
 * render.php previews a slider the visitor never sees. The stricter half of
 * that contract is what matters here — the canvas divides by this value inside
 * a `calc()`, and one unitless number (a gap of `0`, which is exactly what
 * UnitControl stores when the field is cleared) makes the whole expression
 * invalid, so the slide-width declaration is dropped and every slide falls back
 * to its content width. That reads as a slider showing all its slides at once,
 * with nothing left over to scroll.
 *
 * @param {string} gap Stored `gap` attribute.
 * @return {string} CSS length or var() reference; the default gap when unusable.
 */
export const getSliderGapValue = (gap) => {
  const raw = typeof gap === "string" ? gap : "";

  if (raw.startsWith("var:preset|spacing|")) {
    const resolved = resolvePresetValue(raw);

    return /^var\(--wp--preset--spacing--[a-z0-9-]+\)$/.test(resolved)
      ? resolved
      : SLIDER_GAP_FALLBACK;
  }

  return /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(raw)
    ? raw
    : SLIDER_GAP_FALLBACK;
};

/**
 * Core's Block spacing value, resolved to something a `gap` can use.
 *
 * Core only ever writes blockGap through the layout support, so a block that
 * draws its own flex or grid has to read the attribute itself. Mirrors
 * Helpers::get_block_gap_value() in PHP — a block whose editor and render.php
 * disagree here shows one gap while editing and another on the front end.
 *
 * @param {string|Object} blockGap Stored `style.spacing.blockGap` value.
 * @return {string} CSS length or var() reference; empty string when unusable.
 */
export const getBlockGapValue = (blockGap) => {
  // Axis-split values arrive as an object. The control on these blocks is
  // single-valued, so only a hand-edited block produces one; row spacing is
  // the sensible read.
  const raw =
    blockGap && typeof blockGap === "object" ? blockGap.top : blockGap;

  if (typeof raw !== "string" || !raw) {
    return "";
  }

  const value = resolvePresetValue(raw);

  // A preset expands to a var(); anything else has to be a plain length.
  if (value.startsWith("var(")) {
    return value;
  }

  /*
   * Zero is the one length CSS lets you write without a unit, and it is what
   * the spacing control stores when an author clears the field rather than
   * typing `0px`. Falling through to the unit check would reject it and hand
   * back an empty string, which every caller reads as "not set" — so asking
   * for no gap silently restored the block's default gap.
   *
   * Normalised to `0px`, not `0`, for two reasons: callers comparing against
   * `""` need a value, and a unitless zero is only valid where a bare length
   * is expected. Inside calc() it is not — `100% - 4 * 0` mixes a percentage
   * with a unitless number and invalidates the whole expression, which
   * silently collapses any layout deriving a track width from the gap.
   */
  if (/^0+(\.0+)?$/.test(value)) {
    return "0px";
  }

  return /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(value) ? value : "";
};

export const COLOR_PRESET_PREFIX = "var:preset|color|";

/*
 * The compiled form of a colour preset, e.g.
 * `var(--wp--preset--color--brandy-primary, #0061FE)`.
 *
 * Stopping the slug at `,` or `)` keeps the optional fallback out of the capture
 * without trying to balance parentheses — a fallback may itself be a function
 * value like `rgb(0, 0, 0)`, which a lazy `[^)]*` would truncate.
 */
const COMPILED_COLOR_PRESET = /^var\(\s*--wp--preset--color--([a-z0-9_-]+)\s*[,)]/i;

/**
 * The palette slug a stored colour refers to, whichever form it is stored in.
 *
 * Two forms reach this. `var:preset|color|<slug>` is what the controls write —
 * WordPress' own serialisation, and the only one that survives a retheme.
 * `var(--wp--preset--color--<slug>, #hex)` is compiled CSS: the output end of
 * the pipeline, which some older block.json defaults were seeded with directly.
 * The second is recognised so those blocks still resolve to the swatch they were
 * always meant to point at, and so touching the control rewrites them to the
 * canonical form.
 *
 * @param {string} value Stored attribute value.
 * @return {string} Palette slug, or '' when the value is not a preset reference.
 */
const colorPresetSlug = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  if (value.startsWith(COLOR_PRESET_PREFIX)) {
    return value.slice(COLOR_PRESET_PREFIX.length);
  }

  return value.match(COMPILED_COLOR_PRESET)?.[1] ?? "";
};

/**
 * Record which palette swatch a colour came from.
 *
 * WordPress' colour controls report the resolved value — choosing "Primary"
 * hands back `#2F70B3`, with nothing left to say which swatch produced it. Saved
 * that way the block is pinned to a hex, so retheming the site or switching a
 * style variation moves every preset except the ones already baked into content.
 * Matching the value back to its swatch is the last point where the slug can
 * still be recovered.
 *
 * A custom colour matches nothing and passes through unchanged.
 *
 * @param {string} value   Value reported by the colour control.
 * @param {Array}  palette Flat palette entries ({ slug, color }).
 * @return {string} `var:preset|color|<slug>`, or the original value.
 */
export const toColorPreset = (value, palette = []) => {
  if (!value) {
    return value;
  }

  const match = palette.find((entry) => entry?.color === value);

  return match ? `${COLOR_PRESET_PREFIX}${match.slug}` : value;
};

/**
 * The inverse: the concrete colour a stored preset currently points at.
 *
 * Colour controls need a real value to mark a swatch selected and to paint a
 * preview; none of them understand `var:preset|color|…`. A slug whose preset no
 * longer exists — theme switch, renamed entry — falls through unchanged rather
 * than blanking the control.
 *
 * Use this for control state only. To emit CSS, use resolvePresetValue(), which
 * keeps the value a live reference instead of freezing today's colour.
 *
 * @param {string} value   Stored attribute value.
 * @param {Array}  palette Flat palette entries ({ slug, color }).
 * @return {string} Concrete colour, or the original value.
 */
export const fromColorPreset = (value, palette = []) => {
  const slug = colorPresetSlug(value);

  if (!slug) {
    return value;
  }

  return palette.find((entry) => entry?.slug === slug)?.color ?? value;
};

/**
 * Build the Phosphor class list for a glyph.
 *
 * `ph ph-{name}` for the regular weight, `ph-{weight} ph-{name}` otherwise.
 * IconCssService greps rendered pages for exactly this shape to decide which
 * font-face rules to emit, which is also why every block that can output it
 * must appear in that service's ICON_BLOCKS list.
 *
 * @param {string} name Glyph name.
 * @param {string} type Weight: regular, fill or duotone.
 * @return {string} Class list, or '' when no glyph is set.
 */
export const getIconClasses = (name, type) => {
  if (!name) {
    return "";
  }
  return `ph${type && type !== "regular" ? `-${type}` : ""} ph-${name}`;
};

/**
 * Get editor color palette (theme colors + default colors)
 * Use this function with useSelect hook
 *
 * @example
 * import { useSelect } from "@wordpress/data";
 * import { getEditorColorPalette } from "../../utils/helpers";
 *
 * const colors = useSelect(getEditorColorPalette, []);
 */
export const getEditorColorPalette = (select) => {
  const settings = select("core/block-editor")?.getSettings();
  // Merge both theme colors and default colors
  const themeColors = settings?.colors || [];
  const defaultColors =
    settings?.["__experimentalFeatures"]?.color?.palette?.default || [];

  // Combine both arrays, theme colors first
  return [...themeColors, ...defaultColors];
};

/**
 * Core's padding value, mapped onto a block's own CSS custom properties.
 *
 * A block that pads something other than its wrapper has to skip core's
 * serialization and place the value itself, so the four sides arrive here as
 * the raw `style.spacing.padding` object. Each side then needs the same three
 * steps getBlockGapValue applies to a gap: expand a preset reference, which is
 * stored as `var:preset|spacing|<slug>` and is not valid CSS on its own; refuse
 * anything that is not a length, because the value comes from post content and
 * lands in a style attribute where a stray semicolon would close the custom
 * property and open a second declaration; and leave an unset side out entirely,
 * so the stylesheet default keeps applying to it.
 *
 * @param {Object} padding Stored `style.spacing.padding` value.
 * @param {string} prefix  Custom property prefix, e.g. `--panel-padding`.
 * @return {Object} Style object; empty when nothing usable is set.
 */
export const getPaddingVars = (padding, prefix) => {
  const vars = {};

  if (!padding || typeof padding !== "object") {
    return vars;
  }

  ["top", "right", "bottom", "left"].forEach((side) => {
    const raw = padding[side];

    if (typeof raw !== "string" || !raw) {
      return;
    }

    const value = resolvePresetValue(raw);

    // A preset expands to a var(); anything else has to be a plain length.
    if (value.startsWith("var(")) {
      vars[`${prefix}-${side}`] = value;
      return;
    }

    // Zero is the one length CSS lets you write without a unit, and it is what
    // the spacing control stores when an author clears a side rather than
    // typing `0px`. Dropping it would restore the stylesheet default instead.
    if (/^0+(\.0+)?$/.test(value)) {
      vars[`${prefix}-${side}`] = "0px";
      return;
    }

    if (/^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(value)) {
      vars[`${prefix}-${side}`] = value;
    }
  });

  return vars;
};
